import type { CatalogRow, PuntoEntregaDetail, PuntoEntregaParticipanteFormPayload } from '../domain/puntoEntregaModel.ts';
import type { PuntosEntregaRepositoryPort } from '../domain/ports/PuntosEntregaRepositoryPort.ts';
import type { PuntoEntregaLugarOrigen } from '../domain/puntoEntregaLugarOrigen.ts';
import { legacyLugarNombreInComuna } from '../domain/puntoEntregaLugarOrigen.ts';
import { puntoEntregaLatLonValidos, splitDateTime } from '../domain/puntoEntregaFormUtils.ts';

export type PuntosEntregaFormOpenPatch = {
  view: 'form';
  editingId: number | null;
  entregaId: number;
  esRemanente: '' | 'si' | 'no';
  comunaId: number;
  lugarId: number;
  lugarOrigen: PuntoEntregaLugarOrigen | '';
  lugaresOpt: ReadonlyArray<CatalogRow>;
  fecha: string;
  hora: string;
  latitud: string;
  longitud: string;
  direccion: string;
  responsableId: number;
  responsableNombre: string;
  informePdf: string;
  participantesDraft: PuntoEntregaParticipanteFormPayload[];
  feedback: string;
  puntoFormDirty: boolean;
  ubicacionMapaOpen: boolean;
  gpsAccuracyMeters: number | null;
  applyResponsableFromSessionAfter: boolean;
};

export type PuntosEntregaFormOpenPatchSink = Omit<
  PuntosEntregaFormOpenPatch,
  'applyResponsableFromSessionAfter'
>;

export function localDateAndTimeStrings(now: Date): { fecha: string; hora: string } {
  const y = now.getFullYear();
  const mo = String(now.getMonth() + 1).padStart(2, '0');
  const da = String(now.getDate()).padStart(2, '0');
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return { fecha: `${y}-${mo}-${da}`, hora: `${hh}:${mm}` };
}

export function buildCreateFormOpenPatch(
  repo: PuntosEntregaRepositoryPort,
  now: Date = new Date(),
): PuntosEntregaFormOpenPatch {
  const { fecha, hora } = localDateAndTimeStrings(now);
  return {
    view: 'form',
    editingId: null,
    entregaId: 0,
    esRemanente: 'no',
    comunaId: 0,
    lugarId: 0,
    lugarOrigen: '',
    lugaresOpt: [],
    fecha,
    hora,
    latitud: '',
    longitud: '',
    direccion: '',
    responsableId: 0,
    responsableNombre: '',
    informePdf: '',
    participantesDraft: [],
    feedback: '',
    puntoFormDirty: false,
    ubicacionMapaOpen: false,
    gpsAccuracyMeters: null,
    applyResponsableFromSessionAfter: true,
  };
}

export function buildEditFormOpenPatch(
  repo: PuntosEntregaRepositoryPort,
  editingId: number,
  d: PuntoEntregaDetail,
): PuntosEntregaFormOpenPatch {
  const entregas = repo.listEntregas();
  const eid = entregas.some((e) => e.id === d.entregaId) ? d.entregaId : (entregas[0]?.id ?? 0);
  let lugaresOpt = repo.listLugaresByComuna(d.comunaId);
  if (
    d.lugarOrigen === 'legacy' &&
    d.lugarId > 0 &&
    !lugaresOpt.some((l) => l.id === d.lugarId)
  ) {
    const nombre = legacyLugarNombreInComuna(repo, d.comunaId, d.lugarId);
    if (nombre) lugaresOpt = [...lugaresOpt, { id: d.lugarId, nombre }];
  }
  const { fecha, hora } = splitDateTime(d.fechaHora);
  const responsableId = d.responsableId;
  const responsableNombre = d.responsableNombre || '';

  return {
    view: 'form',
    editingId,
    entregaId: eid,
    esRemanente: d.esRemanente ? 'si' : 'no',
    comunaId: d.comunaId,
    lugarId: d.lugarId,
    lugarOrigen: d.lugarOrigen,
    lugaresOpt,
    fecha,
    hora,
    latitud: d.latitud,
    longitud: d.longitud,
    direccion: d.direccion,
    responsableId,
    responsableNombre,
    informePdf: d.informePdf,
    participantesDraft: d.participantes.map((p) => ({ ...p })),
    feedback: '',
    puntoFormDirty: false,
    ubicacionMapaOpen: puntoEntregaLatLonValidos(d.latitud, d.longitud),
    gpsAccuracyMeters: null,
    applyResponsableFromSessionAfter: responsableId === 0 && !responsableNombre,
  };
}

export function applyPuntosEntregaFormOpenPatchToSink(
  sink: PuntosEntregaFormOpenPatchSink,
  p: PuntosEntregaFormOpenPatch,
): void {
  sink.view = p.view;
  sink.editingId = p.editingId;
  sink.entregaId = p.entregaId;
  sink.esRemanente = p.esRemanente;
  sink.comunaId = p.comunaId;
  sink.lugarId = p.lugarId;
  sink.lugarOrigen = p.lugarOrigen;
  sink.lugaresOpt = p.lugaresOpt;
  sink.fecha = p.fecha;
  sink.hora = p.hora;
  sink.latitud = p.latitud;
  sink.longitud = p.longitud;
  sink.responsableId = p.responsableId;
  sink.responsableNombre = p.responsableNombre;
  sink.informePdf = p.informePdf;
  sink.participantesDraft = p.participantesDraft;
  sink.feedback = p.feedback;
  sink.puntoFormDirty = p.puntoFormDirty;
  sink.ubicacionMapaOpen = p.ubicacionMapaOpen;
  sink.direccion = p.direccion;
  sink.gpsAccuracyMeters = p.gpsAccuracyMeters;
}
